// Shared HTTP transport for every frontend/backend call (frontend containment F1).
// Owns base URL, credentials, JSON parsing and shared error normalisation. Knows nothing
// about Today, Client, Prospecting or any other business concept.
//
// BASE is exported because 22 call sites build URLs directly (FormData uploads, EventSource,
// WebSocket) rather than going through request(). It is NOT re-exported by lib/api.ts, so the
// public surface of that module is unchanged.
//
// The auth-expired path imports ../stores dynamically on purpose: that keeps this module free
// of any static dependency on the store layer.

// Relative on purpose. In production the SPA is served from app.dealton.ai and
// nginx proxies /api/ to the backend, so this resolves to the same URL it always
// did (same-origin). Locally, vite proxies /api to the sandbox backend on :8433
// (see vite.config.ts), which is what keeps `npm run dev` off the production
// database. Safe as shipped because the product is web-only — the Tauri desktop
// shell is vestigial (DEVELOPMENT.md); a real desktop bundle would need an
// absolute origin here since it loads from tauri://localhost, not app.dealton.ai.
export const BASE = '/api';

export async function request<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    ...opts,
  });
  if (!res.ok) {
    if (res.status === 401) {
      // Session expired — redirect to login
      const { clearCachedUserData } = await import('../stores');
      clearCachedUserData();
      window.location.reload();
      throw new Error('Session expired');
    }
    const body = await res.json().catch(() => null);
    const detail = body?.detail || '';
    if (detail.includes('GMAIL_AUTH_EXPIRED') || detail.includes('MICROSOFT_AUTH_EXPIRED')) {
      alert('Email authorization expired. Please sign in again to reconnect.');
      const { clearCachedUserData } = await import('../stores');
      clearCachedUserData();   // before the fetch — see App.svelte:logout
      await fetch(`${BASE}/auth/logout?clear_gmail=true`, { method: 'POST', credentials: 'include' });
      window.location.reload();
      throw new Error('Email auth expired');
    }
    // Carry the status on the error — it is NOT readable off the message, because
    // `detail` replaces it whenever the server sends one. Callers route 402 to the paywall.
    const fail = (msg: string) => {
      const err = new Error(msg) as Error & { status: number };
      err.status = res.status;
      return err;
    };
    if (res.status === 429) {
      throw fail(detail || 'Rate limited — please wait and try again later.');
    }
    if (res.status === 503) {
      throw fail(detail || 'Anthropic temporarily unavailable. Please try again in a minute.');
    }
    throw fail(detail || `${res.status} ${res.statusText}`);
  }
  return res.json();
}
